import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Check, Plug, Sparkles, X } from 'lucide-react';
import { toast } from 'sonner';
import {
  AI_EFFORTS,
  AI_EFFORT_LABELS,
  AI_PROVIDER_OPTIONS,
  aiProviderOption,
  formatDateTime,
  messages,
  type AiEffort,
  type IntegrationStatus,
  type OrgSettings,
  type TimerexCalendar,
  type TimerexConfig,
} from '@nesc/shared';
import { api, errorMessage, type SingleResponse } from '@/lib/api-client';
import { qk } from '@/lib/query-client';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Field } from '@/components/ui/field';
import { Input, Select } from '@/components/ui/input';
import { SkeletonRows } from '@/components/ui/skeleton';
import { ConfirmDialog } from '@/components/common/feedback';
import { PageHeader } from '@/components/common/page';

/** S74 外部連携（admin のみ） */
export const SettingsIntegrationsPage = () => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [editing, setEditing] = useState<IntegrationStatus | null>(null);
  const [apiKey, setApiKey] = useState('');

  const { data, isLoading } = useQuery({
    queryKey: qk.integrations,
    queryFn: () => api.get<SingleResponse<IntegrationStatus[]>>('/settings/integrations'),
  });

  const test = useMutation({
    mutationFn: (provider: string) =>
      api.post<SingleResponse<{ ok: boolean; message: string }>>(
        `/settings/integrations/${provider}/test`,
      ),
    onSuccess: (result) => {
      if (result.data.ok) toast.success(messages.settings.testSucceeded, { description: result.data.message });
      else toast.error(messages.settings.testFailed, { description: result.data.message });
      void queryClient.invalidateQueries({ queryKey: qk.integrations });
    },
    onError: (error) => toast.error(errorMessage(error)),
  });

  const save = useMutation({
    mutationFn: () => api.put(`/settings/integrations/${editing?.provider}`, { apiKey }),
    onSuccess: () => {
      toast.success('設定を更新しました');
      void queryClient.invalidateQueries({ queryKey: qk.integrations });
      setEditing(null);
      setApiKey('');
    },
    onError: (error) => toast.error(errorMessage(error)),
  });

  return (
    <>
      <PageHeader back="/settings" title={messages.settings.integrations} />

      <AiUsageSettings integrations={data?.data ?? []} />

      {isLoading ? (
        <SkeletonRows rows={5} />
      ) : (
        <ul className="space-y-3">
          {data?.data.map((integration) => (
            <li key={integration.provider}>
              <Card>
                <CardHeader>
                  <div className="min-w-0">
                    <CardTitle className="flex flex-wrap items-center gap-2">
                      <Plug aria-hidden className="size-5" />
                      {integration.label}
                      {integration.configured ? (
                        <span className="inline-flex items-center gap-1 rounded-control border border-border-strong px-2 text-note text-foreground-muted">
                          <Check aria-hidden className="size-3.5" />
                          {messages.settings.keyRegistered}
                        </span>
                      ) : (
                        <span className="inline-flex items-center gap-1 rounded-control border-2 border-foreground px-2 text-note font-semibold">
                          <X aria-hidden className="size-3.5" />
                          {messages.settings.keyNotRegistered}
                        </span>
                      )}
                    </CardTitle>
                    <p className="mt-1 text-note text-foreground-muted">
                      {integration.maskedKey && <span className="tabular">{integration.maskedKey}・</span>}
                      {integration.lastTestedAt ? (
                        <>
                          {messages.settings.lastTestedAt}{' '}
                          <span className="tabular">{formatDateTime(integration.lastTestedAt)}</span>
                          {integration.lastTestResult === 'success' ? '（成功）' : '（失敗）'}
                        </>
                      ) : (
                        '未テスト'
                      )}
                    </p>
                    {integration.lastTestMessage && (
                      <p className="mt-1 text-note text-foreground-muted">{integration.lastTestMessage}</p>
                    )}
                  </div>
                  <div className="flex shrink-0 gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => test.mutate(integration.provider)}
                      disabled={test.isPending}
                    >
                      {messages.settings.testConnection}
                    </Button>
                    {/* メールの送受信と Google Workspace は項目が多いため専用画面で設定する */}
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() =>
                        integration.provider === 'mail_send'
                          ? navigate('/settings/mail')
                          : integration.provider === 'mail_receive'
                            ? navigate('/settings/mail-receive')
                            : integration.provider === 'google_workspace'
                              ? navigate('/settings/google-workspace')
                              : setEditing(integration)
                      }
                    >
                      {messages.common.edit}
                    </Button>
                  </div>
                </CardHeader>
                {integration.provider === 'timerex' ? (
                  <CardContent>
                    <TimerexSettings integration={integration} />
                  </CardContent>
                ) : (
                  Object.keys(integration.config).length > 0 && (
                    <CardContent>
                      <dl className="space-y-1 text-note">
                        {Object.entries(integration.config).map(([key, value]) => (
                          <div key={key} className="flex gap-2">
                            <dt className="w-32 shrink-0 text-foreground-muted">{key}</dt>
                            <dd className="min-w-0 flex-1 break-all">{String(value)}</dd>
                          </div>
                        ))}
                      </dl>
                    </CardContent>
                  )
                )}
              </Card>
            </li>
          ))}
        </ul>
      )}

      <ConfirmDialog
        open={!!editing}
        onOpenChange={(open) => {
          if (!open) {
            setEditing(null);
            setApiKey('');
          }
        }}
        title={`${editing?.label} の設定`}
        summary={messages.settings.keyMasked}
        detail={
          <Field label="API キー / 認証情報">
            {(props) => (
              <Input
                {...props}
                type="password"
                autoComplete="off"
                value={apiKey}
                onChange={(e) => setApiKey(e.target.value)}
                placeholder="新しい値を入力"
              />
            )}
          </Field>
        }
        confirmText={messages.common.save}
        onConfirm={() => save.mutate()}
        isPending={save.isPending}
      />
    </>
  );
};

type AiForm = { provider: string; model: string; effort: AiEffort };

/** AI の利用設定（プロバイダ・モデル・推論の深さ）。API キーは各連携のカードで登録する */
const AiUsageSettings = ({ integrations }: { integrations: IntegrationStatus[] }) => {
  const queryClient = useQueryClient();
  const [form, setForm] = useState<AiForm>({ provider: 'mock', model: '', effort: 'high' });

  const { data: settings } = useQuery({
    queryKey: qk.settings,
    queryFn: () => api.get<SingleResponse<{ settings: OrgSettings }>>('/settings'),
  });

  useEffect(() => {
    if (!settings) return;
    const ai = settings.data.settings.ai;
    setForm({ provider: ai.provider, model: ai.model, effort: ai.effort });
  }, [settings]);

  const save = useMutation({
    mutationFn: () => api.patch('/settings', { ai: form }),
    onSuccess: () => {
      toast.success(messages.common.saved);
      void queryClient.invalidateQueries({ queryKey: qk.settings });
    },
    onError: (error) => toast.error(errorMessage(error)),
  });

  const option = aiProviderOption(form.provider);
  const keyIntegration = option.integration
    ? integrations.find((i) => i.provider === option.integration)
    : null;
  const keyMissing = !!option.integration && !!keyIntegration && !keyIntegration.configured;

  return (
    <Card className="mb-4">
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Sparkles aria-hidden className="size-5" />
          {messages.settings.aiSettings}
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        <p className="text-note text-foreground-muted">{messages.settings.aiSettingsNote}</p>

        <Field label={messages.settings.aiProvider}>
          {(props) => (
            <Select
              {...props}
              value={form.provider}
              onChange={(e) => {
                const next = aiProviderOption(e.target.value);
                setForm((p) => ({ ...p, provider: next.value, model: next.defaultModel }));
              }}
            >
              {AI_PROVIDER_OPTIONS.map((p) => (
                <option key={p.value} value={p.value}>
                  {p.label}
                </option>
              ))}
            </Select>
          )}
        </Field>

        {form.provider !== 'mock' && (
          <>
            <Field label={messages.settings.aiModel} hint={messages.settings.aiModelNote} required>
              {(props) => (
                <>
                  <Input
                    {...props}
                    list="ai-model-candidates"
                    value={form.model}
                    onChange={(e) => setForm((p) => ({ ...p, model: e.target.value }))}
                    placeholder={option.defaultModel}
                  />
                  <datalist id="ai-model-candidates">
                    {option.models.map((m) => (
                      <option key={m} value={m} />
                    ))}
                  </datalist>
                </>
              )}
            </Field>

            <Field label={messages.settings.aiEffort} hint={messages.settings.aiEffortNote}>
              {(props) => (
                <Select
                  {...props}
                  value={form.effort}
                  onChange={(e) => setForm((p) => ({ ...p, effort: e.target.value as AiEffort }))}
                >
                  {AI_EFFORTS.map((effort) => (
                    <option key={effort} value={effort}>
                      {AI_EFFORT_LABELS[effort]}
                    </option>
                  ))}
                </Select>
              )}
            </Field>

            {keyMissing && (
              <p className="text-note font-semibold">
                {messages.settings.aiKeyMissing(option.label)}
              </p>
            )}
          </>
        )}

        <Button onClick={() => save.mutate()} disabled={save.isPending}>
          {messages.common.save}
        </Button>
      </CardContent>
    </Card>
  );
};

/** 日程調整（TimeRex）：API キー登録後に、予約を受けるカレンダーを選ぶ */
const TimerexSettings = ({ integration }: { integration: IntegrationStatus }) => {
  const queryClient = useQueryClient();
  const config = integration.config as Partial<TimerexConfig>;
  const hasKey = !!integration.maskedKey;
  const [calendarId, setCalendarId] = useState(config.calendarId ?? '');

  useEffect(() => {
    setCalendarId(config.calendarId ?? '');
  }, [config.calendarId]);

  const calendars = useQuery({
    queryKey: ['timerex-calendars'],
    queryFn: () =>
      api.get<SingleResponse<{ team: { id: string; name: string }; calendars: TimerexCalendar[] }>>(
        '/settings/integrations/timerex/calendars',
      ),
    enabled: false,
    retry: false,
  });

  const save = useMutation({
    mutationFn: () => {
      const team = calendars.data?.data.team;
      const calendar = calendars.data?.data.calendars.find((c) => c.id === calendarId);
      if (!team || !calendar) throw new Error(messages.settings.timerexCalendar + 'を選んでください');
      return api.put('/settings/integrations/timerex', {
        config: {
          teamId: team.id,
          teamName: team.name,
          calendarId: calendar.id,
          calendarName: calendar.name,
          calendarUrl: calendar.url,
        },
      });
    },
    onSuccess: () => {
      toast.success(messages.common.saved);
      void queryClient.invalidateQueries({ queryKey: qk.integrations });
    },
    onError: (error) => toast.error(errorMessage(error)),
  });

  const list = calendars.data?.data.calendars ?? [];

  return (
    <div className="space-y-3">
      <p className="text-note text-foreground-muted">{messages.settings.timerexNote}</p>
      {config.calendarName && (
        <p className="text-body">
          {messages.settings.timerexCalendar}：<span className="font-semibold">{config.calendarName}</span>
          {config.teamName && <span className="text-foreground-muted">（チーム {config.teamName}）</span>}
        </p>
      )}
      {!hasKey ? (
        <p className="text-note font-semibold">API キーを登録すると、カレンダーを選べます</p>
      ) : (
        <div className="space-y-3">
          <Button
            variant="outline"
            size="sm"
            onClick={() => void calendars.refetch()}
            disabled={calendars.isFetching}
          >
            {messages.settings.timerexLoadCalendars}
          </Button>
          {calendars.error && <p className="text-note font-semibold">{errorMessage(calendars.error)}</p>}
          {calendars.data && list.length === 0 && (
            <p className="text-note text-foreground-muted">{messages.settings.timerexNoCalendars}</p>
          )}
          {list.length > 0 && (
            <>
              <Field label={messages.settings.timerexCalendar} hint={messages.settings.timerexCalendarNote} required>
                {(props) => (
                  <Select {...props} value={calendarId} onChange={(e) => setCalendarId(e.target.value)}>
                    <option value="">選んでください</option>
                    {list.map((c) => (
                      <option key={c.id} value={c.id}>
                        {c.folderName ? `${c.folderName} / ` : ''}
                        {c.name}（{c.duration} 分）
                      </option>
                    ))}
                  </Select>
                )}
              </Field>
              <Button size="sm" onClick={() => save.mutate()} disabled={!calendarId || save.isPending}>
                {messages.common.save}
              </Button>
            </>
          )}
        </div>
      )}
    </div>
  );
};
